// use client: newsList state + the random `seen` counter computed after mount
'use client'

import type { HomeNewsItem } from '@/types/cms'

import '@/views/personal/personal.css'

import { newsList as localfull_news } from '@/constants/full_news'

import { useEffect, useState } from 'react'
import { Card } from 'react-bootstrap'

import alrajhi_logo from '@/assets/images/alrajhi_logo.svg'
import arrow_right_circle from '@/assets/images/arrow_right_circle.svg'
import circle_left from '@/assets/images/circle_left.svg'
import circle_right from '@/assets/images/circle_right.svg'
import eye from '@/assets/images/eye.svg'
import { PrimaryButton } from '@/components/buttons'
import { CardCarousel } from '@/components/carousel/CardCarousel'
import Link from '@/components/Link'

// Swiper breakpoints are min-width floors, so these props are the <=480px case
// and each entry below overrides upward. The old react-slick config expressed
// the same thing as max-width ceilings (450 / 767 / 991 / 1399), inverted.
//
// The <=480 band is `auto` because personal.css pins the slide to 320px there
// (it used to do so with `!important` over slick's computed width); handing the
// width to CSS is what lets Swiper measure the track correctly. `spaceBetween`
// replaces that rule's `margin: 0 10px`.
const BREAKPOINTS = {
  481: { slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 0 },
  768: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 0 },
  992: { slidesPerView: 3, slidesPerGroup: 3, spaceBetween: 0 }
}

const News = ({ initialData = null }: { initialData?: HomeNewsItem[] | null }) => {
  // Seeded at build time (CMS news + local constant) so the carousel renders in
  // the static HTML; the remote news.json is merged in on the client.
  const [newsList, setNewsList] = useState<HomeNewsItem[]>(
    initialData ? [...initialData, ...localfull_news] : localfull_news
  )

  // Content comes from the build-time snapshot only (seeded above). A Strapi
  // publish rebuilds and redeploys the site, so there is nothing to revalidate on
  // mount. See tests/no-client-cms-fetch.spec.ts.
  //
  // This effect survives the fetch removal because of `seen`: it is a decorative
  // view counter with no CMS source, and `Math.random()` must run AFTER mount —
  // computing it during render would bake one value into the static HTML and
  // mismatch on hydration.
  useEffect(() => {
    setNewsList(current =>
      current.map(item => ({
        ...item,
        seen: item.seen || Math.floor(Math.random() * 100) + 1
      }))
    )
  }, [])

  return (
    <section className="section news">
      <div className="container">
        <div className="text-center">
          <h2 className="fw-bolder">News & announcements</h2>
        </div>
        <div className="tag-container text-center pt-3"></div>
      </div>

      <div className="container news-content pt-5">
        <CardCarousel
          slidesPerView="auto"
          slidesPerGroup={1}
          spaceBetween={20}
          autoHeight
          breakpoints={BREAKPOINTS}
          arrows={{ prevSrc: circle_left.src, nextSrc: circle_right.src }}
          arrowsClassName="carousel-arrow--hide-mobile"
        >
          {newsList.map(news => (
            <div className="personal-promotion-item" key={news.slug}>
              <Card
                style={{ border: 'none', borderRadius: '20px' }}
                className="shadow"
                data-aos="fade-left"
                data-aos-delay={`${(news.id ?? 0) + 1}00`}
              >
                <Card.Img
                  loading="lazy"
                  variant="top"
                  src={news.image || alrajhi_logo.src}
                  alt={news.title || 'News image'}
                  style={{
                    width: '100%',
                    height: 175,
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    objectFit: news.image ? `cover` : `scale-down`
                  }}
                />
                <Card.Body
                  style={{
                    gap: '24px',
                    display: 'flex',
                    alignSelf: 'stretch',
                    flexDirection: 'column',
                    padding: '16px 24px 20px 24px'
                  }}
                >
                  <div className="d-flex flex-row justify-content-between align-items-center">
                    <div
                      className={`news-badge ${news.type}`}
                      style={{ backgroundColor: news.background, borderColor: news.background }}
                    >
                      {news.type}
                    </div>
                    <div className="news-date">{news.date}</div>
                  </div>
                  <div className="title">
                    {(news.title ?? '').length < 100
                      ? news.title
                      : (news.title ?? '').slice(0, 100) + '...'}
                  </div>
                  <div className="d-flex flex-row justify-content-between align-items-center pt-2">
                    <div className="d-flex flex-row align-items-center">
                      <img src={eye.src} height="24px" width="24px" alt="eye" />
                      <span
                        className="news-views"
                        style={{ paddingLeft: '8px', paddingTop: '4px' }}
                      >
                        {news.seen}
                      </span>
                    </div>
                    <Link
                      className="d-flex flex-row align-items-center news-readmore text-decoration-none"
                      href={`/news/${news.slug}`}
                      aria-label={`Read more: ${news.title}`}
                    >
                      <span>Read more</span>
                      {/* In innerText (what Lighthouse's link-text audit reads)
                          but visually hidden — makes the link descriptive. */}
                      <span className="visually-hidden">: {news.title}</span>
                      <img
                        src={arrow_right_circle.src}
                        width="24"
                        height="24"
                        alt=""
                        style={{ marginLeft: '8px' }}
                      />
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </div>
          ))}
        </CardCarousel>
      </div>

      <div className="text-center">
        <PrimaryButton text="View all" href="/news" ariaLabel="View all news and announcements" />
      </div>
    </section>
  )
}

export default News
